
import React, { useMemo, useState } from 'react';
import { Contact, InspectionItem } from '../types';
import { AlertTriangle, CheckCircle, BarChart3, Clock, TrendingUp, PieChart as PieIcon, GitMerge, X } from 'lucide-react';
import { CompanyMergeAssistant } from './analysis/CompanyMergeAssistant';
import { collectCompanyUsage, suggestCompanyMerges } from '../services/companyMergeLogic';
import { countInspectionStats, selectItemsForCompanyStats } from '../services/pdf/pdfUtils';

interface Props {
  items: InspectionItem[];
  /**
   * Schreibt geaenderte Maengel zurueck. Fehlt der Callback, ist die Auswertung
   * reine Anzeige — dann erscheint das Zusammenfuehren gar nicht erst.
   */
  onItemsChange?: (items: InspectionItem[]) => void;
  /** Projektverteiler: Quelle fuer die „offizielle" Firmen-Schreibweise. */
  contacts?: Contact[];
}

type CompanyStats = { open: number; closed: number; good: number; total: number };

/**
 * Die Ziel-Schreibweisen im Klartext. Die frühere Meldung („auf die gewählte
 * Schreibweise umgestellt") stimmte im Plural nicht und verschwieg gerade das,
 * was der Nutzer nachprüfen will: WORAUF die Mängel jetzt laufen. Ab vier
 * Zielen wird gekürzt, sonst wird aus der Meldung eine Liste.
 */
const formatTargets = (targets: string[]): string => {
  const quoted = targets.map(t => `„${t}"`);
  if (quoted.length <= 1) return quoted[0] || '';
  if (quoted.length <= 3) return `${quoted.slice(0, -1).join(', ')} und ${quoted[quoted.length - 1]}`;
  const rest = quoted.length - 3;
  return `${quoted.slice(0, 3).join(', ')} und ${rest} ${rest === 1 ? 'weiterer Firma' : 'weiteren Firmen'}`;
};

export const AnalysisView: React.FC<Props> = ({ items, onItemsChange, contacts = [] }) => {
  const [showMerge, setShowMerge] = useState(false);
  const [mergeNotice, setMergeNotice] = useState<string | null>(null);

  // Nur die real vergebenen Firmennamen — „Nicht zugewiesen" ist keine Firma.
  const companyUsages = useMemo(() => collectCompanyUsage(items), [items]);
  const mergeSuggestions = useMemo(() => suggestCompanyMerges(companyUsages), [companyUsages]);

  // Dieselbe Quelle wie die Firmen-Vorschlaege im Mangel-Formular.
  const companySuggestions = useMemo(
    () => Array.from(new Set(contacts.map(c => c.company).filter(Boolean))).sort(),
    [contacts]
  );

  // Zusammenfuehren lohnt erst ab zwei Firmen — auch ohne automatischen Vorschlag,
  // denn die Aehnlichkeitssuche laesst Faelle wie „& " gegen „und" bewusst liegen.
  const canMerge = !!onItemsChange && companyUsages.length >= 2;

  // Dieselbe Zählweise wie die Statistik im PDF (generatorStandard) — die beiden
  // Zahlenblöcke liegen dem Nutzer nebeneinander vor und dürfen sich nicht
  // widersprechen.
  const { total, open, info, general, done, progressPercent } = countInspectionStats(items);

  // Group by responsible company
  // Ohne „Hinweis" und „Information": beide zählten nur auf `total` und drückten
  // damit die Quote der Zeile (siehe selectItemsForCompanyStats). Die
  // PDF-Statistik filtert identisch, damit Bildschirm und Ausdruck dieselben
  // Prozente zeigen.
  const companyStats = selectItemsForCompanyStats<InspectionItem>(items).reduce((acc, item) => {
    // Gruppiert wird auf dem ROHEN String (nur so lässt er sich später exakt
    // zusammenführen). Getrimmt wird ausschließlich die Frage, ob überhaupt eine
    // Firma eingetragen ist — sonst bekäme ein Wert aus reinen Leerzeichen eine
    // eigene, optisch leere Zeile, die der Assistent gar nicht anbietet.
    const company = item.responsiblePerson?.trim() ? item.responsiblePerson : 'Nicht zugewiesen';
    if (!acc[company]) {
      acc[company] = { open: 0, closed: 0, good: 0, total: 0 };
    }
    acc[company].total++;
    if (item.status === 'open') acc[company].open++;
    if (item.status === 'closed') acc[company].closed++;
    if (item.status === 'good') acc[company].good++;
    return acc;
  }, {} as Record<string, CompanyStats>);

  const sortedCompanies = (Object.entries(companyStats) as [string, CompanyStats][])
    .sort(([, a], [, b]) => b.open - a.open); 

  // Find overdue items
  const today = new Date().toISOString().split('T')[0];
  const overdueItems = items.filter(i => i.status === 'open' && i.deadline && i.deadline < today);

  // Donut Chart logic
  const pOpen = total > 0 ? (open / total) * 100 : 0;
  const pInfo = total > 0 ? (info / total) * 100 : 0;
  const pGeneral = total > 0 ? (general / total) * 100 : 0;
  // Conic Gradient for Donut
  // open (red) -> info (yellow) -> Information (blau) -> done (green)
  // Information braucht ein eigenes Segment, sonst landet es stillschweigend im
  // gruenen "erledigt"-Rest.
  const donutGradient = `conic-gradient(
    #ef4444 0% ${pOpen}%,
    #fbbf24 ${pOpen}% ${pOpen + pInfo}%,
    #2563eb ${pOpen + pInfo}% ${pOpen + pInfo + pGeneral}%,
    #10b981 ${pOpen + pInfo + pGeneral}% 100%
  )`;

  if (total === 0) {
      return (
          <div className="text-center py-20 text-slate-500 animate-fade-in">
              <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium">Keine Daten verfügbar</h3>
              <p>Fügen Sie Mängel hinzu, um die Auswertung zu sehen.</p>
          </div>
      );
  }

  return (
    // Fragment statt eines gemeinsamen Wrappers: `animate-slide-up` hinterlässt
    // ein transform, und ein transform macht die Karte zum Bezugsrahmen für
    // position:fixed. Der Assistent muss deshalb außerhalb dieses Elements
    // liegen, sonst klebt das Modal an der Auswertungsspalte statt am Fenster.
    <>
    <div className="space-y-6 animate-slide-up max-w-6xl mx-auto pb-10">

      {/* 1. Header Overview with Donut */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Status Donut Card */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-between">
             <div>
                <h3 className="text-lg font-bold text-blue-800 mb-1">Status Verteilung</h3>
                <p className="text-xs text-slate-500 mb-4">{total} Einträge gesamt</p>
                <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500"></div> Offen: <span className="font-bold">{open}</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-yellow-400"></div> Hinweis: <span className="font-bold">{info}</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-600"></div> Information: <span className="font-bold">{general}</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500"></div> Erledigt: <span className="font-bold">{done}</span></div>
                </div>
             </div>
             <div className="relative w-32 h-32 rounded-full flex items-center justify-center shadow-inner" style={{ background: donutGradient }}>
                 <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <PieIcon className="w-8 h-8 text-slate-300" />
                 </div>
             </div>
          </div>

          {/* Progress Card */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-6 text-white flex flex-col justify-between relative overflow-hidden">
                <div className="z-10">
                    <div className="bg-white/20 w-fit px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm mb-2">
                        Abarbeitungsquote
                    </div>
                    <div className="flex flex-col">
                        <span className="text-5xl font-extrabold tracking-tight leading-none mb-1">{progressPercent}%</span>
                        <span className="text-blue-200 text-sm font-medium">erledigt</span>
                    </div>
                </div>
                <div className="z-10 mt-4">
                    <div className="w-full bg-black/20 rounded-full h-2">
                        <div className="bg-white h-full rounded-full transition-all duration-1000" style={{width: `${progressPercent}%`}}></div>
                    </div>
                </div>
                <TrendingUp className="absolute bottom-0 right-0 w-32 h-32 text-white/10 -mr-6 -mb-6" />
          </div>

           {/* Quick Stats */}
          <div className="grid grid-rows-2 gap-4">
               <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold text-emerald-600 uppercase">Positiv & Erledigt</p>
                        <p className="text-2xl font-bold text-emerald-800">{done}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-emerald-400 opacity-50" />
               </div>
               <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center justify-between">
                    <div>
                        <p className="text-xs font-bold text-red-600 uppercase">Handlungsbedarf</p>
                        <p className="text-2xl font-bold text-red-800">{open}</p>
                    </div>
                    <AlertTriangle className="w-10 h-10 text-red-400 opacity-50" />
               </div>
          </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 2. Company Stats Table */}
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
             <div className="flex items-start justify-between gap-3 mb-6">
                 <div>
                     <h3 className="text-lg font-bold text-blue-800 flex items-center">
                         <BarChart3 className="w-5 h-5 mr-2 text-slate-500" />
                         Mängelübersicht nach Firmen
                     </h3>
                     {/* Erklaert, warum eine Firma ganz fehlen kann und warum die
                       * Quoten von der Gesamtzahl abweichen. */}
                     <p className="text-xs text-slate-400 mt-0.5 ml-7">(ohne Hinweise und Informationen)</p>
                 </div>
                 {canMerge && (
                     <button
                         type="button"
                         onClick={() => { setMergeNotice(null); setShowMerge(true); }}
                         title="Schreibvarianten derselben Firma zusammenführen"
                         className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors"
                     >
                         <GitMerge className="w-4 h-4 text-slate-400" />
                         Firmen zusammenführen
                         {mergeSuggestions.length > 0 && (
                             <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">
                                 {mergeSuggestions.length}
                             </span>
                         )}
                     </button>
                 )}
             </div>
             {mergeNotice && (
                 <div className="mb-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                     <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                     <span className="flex-1">{mergeNotice}</span>
                     <button onClick={() => setMergeNotice(null)} className="p-0.5 rounded hover:bg-emerald-100" title="Ausblenden">
                         <X className="w-4 h-4" />
                     </button>
                 </div>
             )}
             <div className="overflow-x-auto">
                 <table className="w-full text-sm">
                     <thead>
                         <tr className="border-b border-slate-100 text-left text-slate-500 uppercase text-xs">
                             <th className="pb-3 pl-2">Firma</th>
                             <th className="pb-3 text-center">Gesamt</th>
                             <th className="pb-3 text-center text-red-600">Offen</th>
                             <th className="pb-3 text-center text-green-600">Erledigt</th>
                             <th className="pb-3 text-right pr-2">Quote</th>
                         </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-50">
                         {sortedCompanies.map(([company, stats]) => {
                             const quote = Math.round(((stats.closed + stats.good) / (stats.total || 1)) * 100);
                             return (
                                <tr key={company} className="hover:bg-slate-50 transition-colors">
                                    <td className="py-4 pl-2 font-medium text-slate-700">{company}</td>
                                    <td className="py-4 text-center text-slate-600 font-bold">{stats.total}</td>
                                    <td className="py-4 text-center">
                                        {stats.open > 0 ? (
                                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-md font-bold text-xs">{stats.open}</span>
                                        ) : <span className="text-slate-300">-</span>}
                                    </td>
                                    <td className="py-4 text-center">
                                        {(stats.closed + stats.good) > 0 ? (
                                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md font-bold text-xs">{stats.closed + stats.good}</span>
                                        ) : <span className="text-slate-300">-</span>}
                                    </td>
                                    <td className="py-4 text-right pr-2">
                                        <div className="flex items-center justify-end gap-2">
                                            <span className="text-xs font-medium text-slate-500">{quote}%</span>
                                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div style={{width: `${quote}%`}} className={`h-full rounded-full ${quote === 100 ? 'bg-emerald-500' : quote > 50 ? 'bg-blue-500' : 'bg-red-400'}`}></div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                             );
                         })}
                     </tbody>
                 </table>
             </div>
          </div>

          {/* 3. Overdue Items */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-blue-800 mb-6 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-red-500" />
                  Überfällige Fristen
              </h3>
              
              {overdueItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 bg-emerald-50 rounded-xl border border-emerald-100 text-emerald-800">
                      <CheckCircle className="w-10 h-10 mb-2 opacity-50" />
                      <p className="font-bold">Keine Fristüberschreitungen</p>
                      <p className="text-xs opacity-70">Alle offenen Punkte im Zeitplan.</p>
                  </div>
              ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                      {overdueItems.map(item => {
                          const diffTime = new Date().getTime() - new Date(item.deadline).getTime();
                          const daysOver = Math.floor(diffTime / (1000 * 3600 * 24));
                          
                          return (
                            <div key={item.id} className="p-4 bg-red-50 rounded-xl border border-red-100 group hover:bg-red-100 transition-colors">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="bg-red-200 text-red-800 text-[11px] font-bold px-2 py-0.5 rounded-full">
                                        seit {daysOver} Tagen
                                    </span>
                                    <span className="text-[11px] text-slate-400">#{item.ticketNumber}</span>
                                </div>
                                <p className="text-sm font-bold text-slate-800 line-clamp-2">{item.title}</p>
                                <div className="flex justify-between items-center mt-3 text-xs text-slate-600 border-t border-red-100 pt-2 group-hover:border-red-200">
                                    <span className="truncate max-w-[120px] font-medium">{item.responsiblePerson}</span>
                                    <span className="font-mono text-red-600">
                                      {new Date(item.deadline).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </span>
                                </div>
                            </div>
                          );
                      })}
                  </div>
              )}
          </div>
      </div>
    </div>

    {showMerge && onItemsChange && (
      <CompanyMergeAssistant
        items={items}
        companySuggestions={companySuggestions}
        onClose={() => setShowMerge(false)}
        onApply={(nextItems, changed, targets) => {
          onItemsChange(nextItems);
          setShowMerge(false);
          if (changed === 0) {
            setMergeNotice('Es wurde nichts geändert.');
            return;
          }
          const wohin = targets.length > 0
            ? ` — ${targets.length === 1 ? 'jetzt unter' : 'jetzt unter den Namen'} ${formatTargets(targets)}`
            : '';
          setMergeNotice(
            `Firmenname bei ${changed} ${changed === 1 ? 'Mangel' : 'Mängeln'} geändert${wohin}.`
          );
        }}
      />
    )}
    </>
  );
};
